import { useEffect, useMemo } from "react";
import { CircularProgress, Paper, Typography } from "@mui/material";
import { useHistory, useParams } from "react-router";
import log from "loglevel";
import { Box } from "@mui/system";
import { Helmet } from "react-helmet";

import useFields, { FILTER_TYPES } from "../hooks/useFields";
import useLocation from "../hooks/useLocation";

import EditEntity from "../components/EditEntity";

import EntityOverview from "../views/EntityOverview";

import { addLocation, patchLocation } from "../dao/locations";

import { softDelete } from "../dao/operations";

import useUserPermissionsContext from "../hooks/useUserPermissionsContext";
import UserRights from "../enums/UserRights";
import CreateItemButton from "../components/CreateItemButton";
import EditModes from "../enums/EditModes";
import { ROUTES } from "../enums/Routes";
import { API_ROUTES } from "../enums/api";
import { useSearchParams } from "../hooks/useSearchParams";
import { addSearchParams } from "../utils/dao";
import { formatPageTitle } from "../utils/pageTitle";

const API_ROUTE = API_ROUTES["locations"];

export default function LocationDetail({ title = "", mode = "" }) {
  const history = useHistory();

  // update record only
  // get locationId from the search params
  const { locationId } = useParams();

  // create record only
  // get objectId from the search params
  const { objectId } = useSearchParams();

  const isUpdateMode = mode === EditModes.UPDATE;
  const isCreateMode = mode === EditModes.CREATE;

  const { isAllowedTo, isLoading } = useUserPermissionsContext();

  const isAllowedToCreate = isAllowedTo(
    UserRights.CREATE,
    FILTER_TYPES.location
  );

  const isAllowedToUpdate = isAllowedTo(
    UserRights.UPDATE,
    FILTER_TYPES.location
  );

  const isAllowedToDelete = isAllowedTo(
    UserRights.SOFT_DELETE,
    FILTER_TYPES.location
  );

  const {
    location,
    fetchLocationData,
    isLoading: recordIsLoading,
  } = useLocation(locationId, objectId);

  const [fields] = useFields(
    FILTER_TYPES.location,
    false,
    isCreateMode,
    isUpdateMode
  );

  const searchParams = useMemo(() => {
    return new URLSearchParams(
      Object.entries({
        objectId: location.object_id,
        systemGroupId: location.system_group_id,
      }).filter(([, value]) => value)
    ).toString();
  }, [location.object_id, location.system_group_id]);

  const pageTitle = formatPageTitle(title);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  async function storeLocation(payload) {
    if (isUpdateMode) {
      // Existing Location
      log.trace("about to patch", location.id);
      await patchLocation(payload, location.id).then(() => {
        fetchLocationData();
      });
    } else {
      // Brand new Location
      log.trace("about to create", JSON.stringify(location));
      return addLocation(payload).then((res) => {
        history.push(["/location", res.id].join("/"));
      });
    }
  }

  async function removeLocation(payload) {
    if (isUpdateMode) {
      return softDelete({ route: API_ROUTE, delete: true })(payload).then(
        () => {
          fetchLocationData();
        }
      );
    }
  }

  async function cancelChanges() {
    if (isUpdateMode) {
      fetchLocationData();
    } else {
      history.goBack();
    }
  }

  function restoreDeletedLocation(payload) {
    softDelete({ route: API_ROUTE, delete: false })(payload).then(() => {
      fetchLocationData();
    });
  }

  return !fields || recordIsLoading ? (
    <Box
      sx={{
        marginTop: 3,
        textAlign: "center",
      }}
    >
      <Helmet>
        <title>{pageTitle}</title>
      </Helmet>
      <CircularProgress />
    </Box>
  ) : (
    <>
      <Helmet>
        <title>{pageTitle}</title>
      </Helmet>
      <Paper
        sx={{
          padding: 4,
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            marginBottom: "1rem",
          }}
        >
          <Typography variant='h5'>{title}</Typography>
          {isUpdateMode && (
            <CreateItemButton
              size='medium'
              entity={FILTER_TYPES.location}
              to={addSearchParams(ROUTES.location.create)(searchParams)}
              isDisabled={isAllowedToCreate === false}
            />
          )}
        </Box>
        {isLoading === false && (
          <EditEntity
            {...{
              isAllowedToUpdate,
              isAllowedToDelete,
              isAllowedToCreate,
              entity: location,
              fields,
              storeEntity: storeLocation,
              deleteEntity: removeLocation,
              entityType: FILTER_TYPES.location,
              cancel: cancelChanges,
              restoreEntity: restoreDeletedLocation,
              isUpdateMode,
            }}
          />
        )}
        {isUpdateMode && (
          <EntityOverview
            entity={FILTER_TYPES.equipment}
            title={"Equipments overview"}
            nestedEntity={true}
            parent={{
              object_id: location.object_id,
              location_id: location.id,
            }}
          />
        )}
      </Paper>
    </>
  );
}
